/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  Component,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { fasUserIcon } from 'IconLibrary/fontawesome/solid/user';
import { fasUsersIcon } from 'IconLibrary/fontawesome/solid/users';
import { fasBuildingIcon } from 'IconLibrary/fontawesome/solid/building';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AvatarColors, AvatarColorsMapping, DisplayProps } from '../types';
import { ResumeService } from '../../../resume/resume.service';

/**
 * Renders an avatar for a user, group, or organization. The avatar is composed of a background color and text color.
 * The color or the avatar is determined by the colorScheme fields on the DisplayProps object.
 * When a colorScheme is not provided, the avatar will programatically choose a color based on the id.
 */
@Component({
  selector: 'bulb-user-avatar',
  // We want to use an SVG for the avatar to allow the lettering to scale with the avatar size.
  templateUrl: './user-avatar.component.html',
  styleUrls: ['./user-avatar.component.scss'],
  standalone: false,
})
export class UserAvatarComponent implements OnChanges, OnInit {
  @Input() displayProps?: DisplayProps = {
    id: 'default-display-props',
    colorScheme: 'BLUE',
    subjectType: 'BULB_USER',
    displayName: 'Bulb User',
  };

  public fallbackIcon: SafeHtml;

  public color: AvatarColors;

  private colorOptions = [...AvatarColorsMapping.keys()];

  /**
   * Default Constructor.
   *
   * @param sanitizer
   * @param resumeService Our resume service holds stuff related to resume.
   */
  constructor(
    private sanitizer: DomSanitizer,
    private resumeService: ResumeService,
  ) { }

  ngOnInit() {
    this.setViewProps();
    this.resumeService.avatarDisplayProps = this.displayProps;
  }

  ngOnChanges() {
    this.setViewProps();
  }

  private setViewProps() {
    // We need to inject the SVG markup inside of another SVG.
    // The svg-icon component we generally use will render additional markup
    // that isn't valid inside of an SVG.
    // Instead, we're importing the SVG markup and injecting it into the template.
    if (this.displayProps) {
      if (this.displayProps.subjectType === 'BULB_GROUP') {
        this.fallbackIcon = this.sanitizer.bypassSecurityTrustHtml(fasUsersIcon.data);
      } else if (['BULB_ORGANIZATION', 'BULB_ORG'].includes(this.displayProps.subjectType)) {
        this.fallbackIcon = this.sanitizer.bypassSecurityTrustHtml(fasBuildingIcon.data);
      } else {
        this.fallbackIcon = this.sanitizer.bypassSecurityTrustHtml(fasUserIcon.data);
      }

      this.color = this.displayProps.colorScheme
        ? AvatarColorsMapping.get(this.displayProps.colorScheme)
        : this.getFallbackColor();
    }
  }

  /**
   * Selects a fallback color if no colorScheme is provided.
   * Users get a blue color, Groups and ORgs get a random color generated from their ID.
   */
  private getFallbackColor(): AvatarColors {
    return this.displayProps.subjectType === 'BULB_USER'
      ? AvatarColorsMapping.get('BLUE')
      : this.selectColorFromSeed(this.displayProps.id);
  }

  /**
   * Convert each charater in the seed to a number, and then add them together.
   * This will give us a number that is unique to the seed.
   * We can then use this number to select a color from the color options.
   */
  private selectColorFromSeed(seed: string): AvatarColors {
    const charCodes: string = seed.split('').reduce((a, b) => (a.charCodeAt(0) + b.charCodeAt(0)).toString());
    return AvatarColorsMapping.get(this.colorOptions[parseInt(charCodes, 10) % this.colorOptions.length]);
  }
}
